import React, { useState, useEffect } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { 
  Home,
  Users, 
  FileText, 
  DollarSign,
  BarChart3, 
  Settings, 
  User, 
  LogOut,
  X
} from 'lucide-react';

const AdminSidebar = ({ user, settings, onLogout, isMobileOpen, onToggleMobile }) => {
  const location = useLocation();
  const [mounted, setMounted] = useState(false);
  
  const menuItems = [
    { id: 'dashboard', label: 'Dashboard', icon: Home, path: '/admin/' },
    { id: 'clients', label: 'Clients', icon: Users, path: '/admin/clients' },
    { id: 'invoices', label: 'Invoices', icon: FileText, path: '/admin/invoices' },
    { id: 'quotes', label: 'Quotes', icon: DollarSign, path: '/admin/quotes' },
    { id: 'reports', label: 'Reports', icon: BarChart3, path: '/admin/reports' },
    { id: 'settings', label: 'Settings', icon: Settings, path: '/admin/settings' }
  ];

  useEffect(() => {
    setMounted(true);
  }, []);

  const handleLogout = () => {
    onLogout();
  };

  const handleNavClick = () => {
    // Close mobile menu when navigating
    if (isMobileOpen && onToggleMobile) {
      onToggleMobile();
    }
  };

  // Don't render until mounted to avoid hydration issues
  if (!mounted) {
    return null;
  }

  return (
    <div className="h-full flex flex-col bg-white">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-white">
        <div className="flex items-center space-x-3">
          {settings?.logo && (
            <img 
              src={settings.logo} 
              alt="Logo"
              className="h-8 w-8 object-contain rounded"
            />
          )}
          <span className="text-lg font-semibold text-gray-800 truncate">
            {settings?.name || 'Admin Portal'}
          </span>
        </div>
        {/* Mobile close button */}
        <button 
          className="lg:hidden p-2 rounded-md hover:bg-gray-100 transition-colors"
          onClick={onToggleMobile}
          title="Close menu"
        >
          <X size={20} className="text-gray-600" />
        </button>
      </div>

      {/* User Info */}
      <div className="flex items-center p-4 bg-gray-50 border-b border-gray-200">
        <div className="flex-shrink-0 w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
          <User size={20} className="text-blue-600" />
        </div>
        <div className="ml-3 min-w-0 flex-1">
          <div className="text-sm font-medium text-gray-900 truncate">
            {user?.full_name || user?.username || 'Admin User'}
          </div>
          <div className="text-xs text-gray-500 truncate">
            {user?.email || 'admin@example.com'}
          </div>
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 px-2 py-4 space-y-1 overflow-y-auto">
        {menuItems.map(item => {
          const Icon = item.icon;
          const isActive = 
            location.pathname === item.path || 
            (item.path === '/admin/' && location.pathname === '/admin') ||
            (item.path === '/admin/settings' && location.pathname.startsWith('/admin/settings'));
          
          return (
            <div key={item.id} className="w-full">
              <NavLink
                to={item.path}
                className={({ isActive: routerActive }) => 
                  `group flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors duration-150 ${
                    isActive 
                      ? 'bg-blue-100 text-blue-700 border-r-2 border-blue-500' 
                      : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'
                  }`
                }
                onClick={handleNavClick}
                title={item.label}
              >
                <Icon 
                  size={20} 
                  className={`mr-3 flex-shrink-0 ${
                    isActive ? 'text-blue-700' : 'text-gray-400 group-hover:text-gray-600'
                  }`} 
                />
                <span className="truncate">{item.label}</span>
              </NavLink>
            </div>
          );
        })}
      </nav>

      {/* Footer */}
      <div className="flex-shrink-0 border-t border-gray-200 bg-white">
        <button
          className="w-full flex items-center px-3 py-3 text-sm font-medium text-gray-700 hover:bg-gray-100 hover:text-gray-900 transition-colors duration-150"
          onClick={handleLogout}
          title="Logout"
        >
          <LogOut size={16} className="mr-3 text-gray-400" />
          <span className="truncate">Logout</span>
        </button>
        <div className="px-3 py-2 text-xs text-gray-500 text-center border-t border-gray-100">
          © 2025 KGC Innovations
        </div>
      </div>
    </div>
  );
};

export default AdminSidebar;